import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  HardDrive,
  Play,
  Square,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ArrowRight,
  FolderOpen,
  Chrome,
} from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { BrowserScanner, isFileSystemAccessSupported, pickDirectory } from "../lib/scanner";
import { useAuth } from "../hooks/useAuth";
import { formatBytes, formatDuration, formatNumber } from "../lib/format";
import type { ScanProgressEvent } from "../types";

type Phase = "browser-check" | "pick" | "configure" | "confirm-replace" | "scanning" | "completed" | "failed";

interface PickedRoot {
  handle: FileSystemDirectoryHandle;
  name: string;
}

export default function ScanPage(): JSX.Element {
  const { session } = useAuth();
  const qc = useQueryClient();
  const [phase, setPhase] = useState<Phase>(
    isFileSystemAccessSupported() ? "pick" : "browser-check"
  );
  const [picked, setPicked] = useState<PickedRoot | null>(null);
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [progress, setProgress] = useState<ScanProgressEvent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ diskId: string; filesScanned: number; durationMs: number } | null>(null);
  const scannerRef = useRef<BrowserScanner | null>(null);

  const pickMut = useMutation({
    mutationFn: async () => {
      const handle = await pickDirectory();
      if (!handle) return null;
      return { handle, name: handle.name };
    },
    onSuccess: (next) => {
      if (!next) return;
      setPicked(next);
      setPhase("configure");
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  useEffect(() => {
    if (!picked) return;
    const inferred = (picked.name ?? "").trim();
    if (!inferred) return;
    setLabel((current) => (current.trim() ? current : inferred));
  }, [picked]);

  const startMut = useMutation({
    mutationFn: async ({ replaceExisting }: { replaceExisting: boolean }) => {
      if (!picked) throw new Error("Aucun dossier sélectionné");
      if (!label.trim()) throw new Error("Le label est requis");

      const trimmedLabel = label.trim();
      const existing = await api.findDiskByLabel(trimmedLabel);
      if (existing && !replaceExisting) {
        return { conflict: existing.id } as const;
      }
      if (existing && replaceExisting) {
        await api.deleteDisk(existing.id);
      }

      const disk = await api.createDisk({
        label: trimmedLabel,
        volumeName: picked.name,
        notes: notes.trim() || undefined,
        scannedBy: session?.user?.email ?? undefined,
      });

      const scanner = new BrowserScanner(disk.id, picked.handle, (e) => setProgress(e));
      scannerRef.current = scanner;

      try {
        const r = await scanner.run();
        if (scanner.isCancelled()) {
          await api.deleteDisk(disk.id);
          throw new Error("Scan annulé");
        }
        await api.finalizeDisk(disk.id, {
          fileCount: r.filesScanned,
          usedSize: r.bytesScanned,
        });
        return { ok: true as const, diskId: disk.id, filesScanned: r.filesScanned, durationMs: r.durationMs };
      } catch (err) {
        try {
          await api.deleteDisk(disk.id);
        } catch {
          // ignore cleanup error
        }
        throw err;
      }
    },
    onMutate: () => {
      setPhase("scanning");
      setError(null);
      setProgress(null);
    },
    onSuccess: (res) => {
      if (!res) return;
      if ("conflict" in res) {
        setPhase("confirm-replace");
        return;
      }
      setResult({ diskId: res.diskId, filesScanned: res.filesScanned, durationMs: res.durationMs });
      setPhase("completed");
      qc.invalidateQueries({ queryKey: ["disks"] });
    },
    onError: (err: Error) => {
      setError(err.message);
      setPhase("failed");
    },
  });

  useEffect(() => {
    if (phase !== "scanning") return;
    const handler = (e: BeforeUnloadEvent): string => {
      e.preventDefault();
      e.returnValue = "Un scan est en cours. Quitter va l'interrompre.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [phase]);

  const reset = (): void => {
    scannerRef.current?.cancel();
    scannerRef.current = null;
    setPicked(null);
    setLabel("");
    setNotes("");
    setProgress(null);
    setError(null);
    setResult(null);
    setPhase(isFileSystemAccessSupported() ? "pick" : "browser-check");
  };

  return (
    <div className="space-y-10">
      <section>
        <div className="label-caps mb-2">Nouveau scan</div>
        <h1 className="title-dual text-h1">
          Indexer un disque <span className="dim">depuis ton navigateur</span>
        </h1>
        <p className="mt-3 text-foreground/60 text-lead max-w-2xl">
          Branche le disque, sélectionne le volume dans le sélecteur système, et le scan se fait en
          local dans cet onglet. Les métadonnées poussent direct vers Supabase, aucun fichier ne
          quitte ton Mac.
        </p>
      </section>

      {phase === "browser-check" && <BrowserUnsupported />}
      {phase === "pick" && <PickPanel onPick={() => pickMut.mutate()} pending={pickMut.isPending} error={error} />}
      {phase === "configure" && picked && (
        <ConfigPanel
          picked={picked}
          label={label}
          setLabel={setLabel}
          notes={notes}
          setNotes={setNotes}
          error={error}
          starting={startMut.isPending}
          canStart={label.trim().length > 0 && !startMut.isPending}
          onStart={() => startMut.mutate({ replaceExisting: false })}
          onReset={reset}
        />
      )}
      {phase === "confirm-replace" && (
        <ConfirmReplacePanel
          label={label}
          onReplace={() => startMut.mutate({ replaceExisting: true })}
          onCancel={reset}
        />
      )}
      {(phase === "scanning" || phase === "completed" || phase === "failed") && (
        <ProgressPanel
          phase={phase}
          progress={progress}
          label={label}
          error={error}
          result={result}
          onCancel={() => scannerRef.current?.cancel()}
          onReset={reset}
        />
      )}
    </div>
  );
}

function BrowserUnsupported(): JSX.Element {
  return (
    <section className="card-koloc text-center py-12">
      <Chrome className="mx-auto mb-4 h-10 w-10 text-foreground/30" />
      <h3 className="text-h3 font-semibold">Ce navigateur ne supporte pas le scan</h3>
      <p className="mt-3 text-foreground/60 text-body max-w-md mx-auto">
        Le scan utilise l'API File System Access, disponible dans Chrome, Edge, Brave, Arc et Opera.
        Recherche et navigation marchent partout, y compris Safari et Firefox.
      </p>
      <Link to="/recherche" className="pill-outline mt-6">
        Aller à la recherche
      </Link>
    </section>
  );
}

function PickPanel({
  onPick,
  pending,
  error,
}: {
  onPick: () => void;
  pending: boolean;
  error: string | null;
}): JSX.Element {
  return (
    <section className="card-koloc">
      <div className="label-caps mb-4">1. Sélection du disque</div>
      <div className="rounded-md bg-foreground/5 p-6 text-center">
        <FolderOpen className="mx-auto mb-3 h-8 w-8 text-foreground/40" />
        <h3 className="text-h3 font-semibold">Choisis le disque à indexer</h3>
        <p className="mt-2 text-foreground/60 text-body max-w-md mx-auto">
          macOS va ouvrir un dialogue. Navigue vers <span className="font-mono text-foreground">/Volumes/</span> et
          choisis le disque branché. Le navigateur te demandera l'autorisation de lire son contenu.
        </p>
        <button onClick={onPick} disabled={pending} className="pill-solid mt-6">
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderOpen className="h-4 w-4" />}
          Ouvrir le sélecteur
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-caption text-destructive flex gap-3">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>{error}</div>
        </div>
      )}

      <div className="mt-6 divider" />
      <p className="mt-4 text-caption text-foreground/50">
        Astuce : appuie sur ⌘+Maj+. dans le dialogue Finder pour afficher les volumes cachés ou les
        dossiers commençant par un point.
      </p>
    </section>
  );
}

function ConfigPanel({
  picked,
  label,
  setLabel,
  notes,
  setNotes,
  error,
  starting,
  canStart,
  onStart,
  onReset,
}: {
  picked: PickedRoot;
  label: string;
  setLabel: (s: string) => void;
  notes: string;
  setNotes: (s: string) => void;
  error: string | null;
  starting: boolean;
  canStart: boolean;
  onStart: () => void;
  onReset: () => void;
}): JSX.Element {
  return (
    <section className="card-koloc">
      <div className="mb-5 label-caps">2. Configuration</div>

      <div className="rounded-md bg-foreground/5 p-4 mb-6 flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-md bg-foreground/8 shrink-0">
          <HardDrive className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="truncate text-body font-semibold">{picked.name}</div>
          <div className="text-caption text-foreground/50">Sélection prête à scanner</div>
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <label htmlFor="label" className="label-caps mb-2 block">
            Label du disque
          </label>
          <input
            id="label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="ex: KOLOC_PROJ_2024_001"
            className="input-koloc font-mono"
            autoFocus
          />
          <p className="mt-2 text-caption text-foreground/50">
            Inscris idéalement la même nomenclature que sur l'étiquette physique. Si un disque
            avec ce label existe déjà, on te proposera de le remplacer.
          </p>
        </div>

        <div>
          <label htmlFor="notes" className="label-caps mb-2 block">
            Notes (facultatif)
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Projet, client, date d'archivage…"
            rows={3}
            className="input-koloc resize-none"
          />
        </div>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-caption text-destructive flex gap-3">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>{error}</div>
          </div>
        )}

        <div className="flex items-center gap-3 pt-2">
          <button onClick={onStart} disabled={!canStart} className="pill-solid">
            {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Démarrer le scan
          </button>
          <button onClick={onReset} className="pill-outline" disabled={starting}>
            Choisir un autre disque
          </button>
        </div>
      </div>
    </section>
  );
}

function ConfirmReplacePanel({
  label,
  onReplace,
  onCancel,
}: {
  label: string;
  onReplace: () => void;
  onCancel: () => void;
}): JSX.Element {
  return (
    <section className="card-koloc">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 shrink-0 mt-1 text-foreground/60" />
        <div className="flex-1">
          <h3 className="text-h3 font-semibold">Un disque "{label}" existe déjà</h3>
          <p className="mt-2 text-foreground/60">
            On peut remplacer l'index existant par le nouveau scan. L'ancien index sera supprimé,
            le disque physique reste intact.
          </p>
          <div className="mt-5 flex gap-3">
            <button onClick={onReplace} className="pill-solid">
              Remplacer l'index
            </button>
            <button onClick={onCancel} className="pill-outline">
              Annuler
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProgressPanel({
  phase,
  progress,
  label,
  error,
  result,
  onCancel,
  onReset,
}: {
  phase: Phase;
  progress: ScanProgressEvent | null;
  label: string;
  error: string | null;
  result: { diskId: string; filesScanned: number; durationMs: number } | null;
  onCancel: () => void;
  onReset: () => void;
}): JSX.Element {
  const labelMap: Record<Phase, string> = {
    "browser-check": "",
    pick: "",
    configure: "",
    "confirm-replace": "",
    scanning: "Scan en cours",
    completed: "Scan terminé",
    failed: "Scan échoué",
  };

  return (
    <section className="card-koloc">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="label-caps">Scan</div>
          <h2 className="title-dual text-h2 mt-1">
            {label} <span className="dim">· {labelMap[phase]}</span>
          </h2>
        </div>
        <StatusIcon phase={phase} />
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Fichiers" value={formatNumber(progress?.filesScanned ?? result?.filesScanned ?? 0)} />
        <Stat label="Dossiers" value={formatNumber(progress?.directoriesScanned ?? 0)} />
        <Stat label="Volume scanné" value={formatBytes(progress?.bytesScanned ?? 0)} />
        <Stat label="Durée" value={formatDuration(result?.durationMs ?? 0)} />
      </div>

      {phase === "scanning" && (
        <>
          <div className="progress-track mt-6">
            <div className="progress-bar w-full animate-pulse" style={{ width: "60%" }} />
          </div>

          <div className="mt-6">
            <div className="label-caps mb-2">Derniers chemins</div>
            <div className="space-y-1 font-mono text-caption text-foreground/60 max-h-48 overflow-hidden">
              {(progress?.recentPaths ?? []).map((p, i) => (
                <div key={`${i}-${p}`} className="truncate" style={{ opacity: 1 - i * 0.18 }}>
                  {p}
                </div>
              ))}
              {(!progress?.recentPaths || progress.recentPaths.length === 0) && (
                <div className="text-foreground/30">En attente de la première lecture…</div>
              )}
            </div>
          </div>

          <div className="mt-6 rounded-md bg-foreground/5 p-4 text-caption text-foreground/60">
            Ne ferme pas cet onglet pendant le scan. Tu peux passer en arrière-plan, mais la fenêtre
            doit rester ouverte.
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button onClick={onCancel} className="pill-outline">
              <Square className="h-4 w-4" />
              Annuler
            </button>
          </div>
        </>
      )}

      {phase === "completed" && result && (
        <div className="mt-6 space-y-4">
          <div className="rounded-md border border-foreground/10 bg-success/5 p-4 text-caption text-success flex gap-3">
            <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              {formatNumber(result.filesScanned)} fichiers indexés en {formatDuration(result.durationMs)}.
            </div>
          </div>
          <div className="flex gap-3">
            <Link to={`/disques/${result.diskId}`} className="pill-solid">
              Voir le disque
              <ArrowRight className="h-4 w-4" />
            </Link>
            <button onClick={onReset} className="pill-outline">
              Scanner un autre disque
            </button>
          </div>
        </div>
      )}

      {phase === "failed" && (
        <div className="mt-6 space-y-4">
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-caption text-destructive flex gap-3">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>{error ?? "Le scan a échoué."}</div>
          </div>
          <button onClick={onReset} className="pill-outline">
            Recommencer
          </button>
        </div>
      )}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <div className="label-caps">{label}</div>
      <div className="mt-2 text-h2 num">{value}</div>
    </div>
  );
}

function StatusIcon({ phase }: { phase: Phase }): JSX.Element {
  if (phase === "completed") return <CheckCircle2 className="h-6 w-6 text-success" />;
  if (phase === "failed") return <AlertCircle className="h-6 w-6 text-destructive" />;
  return <Loader2 className="h-6 w-6 animate-spin text-foreground/60" />;
}
